/**
 * A score as a Standard MIDI File.
 *
 * MIDI is a performance, not a page: it carries what is heard and nothing of how it is
 * written. So this is deliberately built on {@link scheduleOf} rather than on the document
 * -- transposition, ties, grace notes and repeats are already resolved there, in the one
 * place those rules live, and a file written from anywhere else would be a second copy of
 * them drifting out of step with playback.
 *
 * Format 1: a conductor track carrying the metre, the key and the speed, then one track per
 * part. That is what every notation program writes and what every one of them expects to
 * read, and it keeps a part identifiable after the round trip rather than merged into one
 * stream of notes.
 *
 * The file is written at this editor's own resolution rather than at the 480 or 960 a
 * sequencer usually picks, because 6720 to the quarter is what the document counts in: any
 * other division would round every triplet and quintuplet on the way out.
 *
 * What MIDI cannot hold is said plainly: articulation, slurs, hairpins, lyrics, chord
 * symbols, figures, layout and spelling are not in the file. A note is a key number, so
 * D flat and C sharp are the same byte, and reading the file back cannot know which was
 * written.
 */

import { PPQ, ticksOf } from "../document/duration";
import { instrumentAt, resolveAttributes } from "../document/query";
import type { Part, Score } from "../document/types";
import { mutedProgram } from "../playback/manner";
import { PIZZICATO_PROGRAM } from "../playback/plucking";
import { scheduleOf } from "../playback/schedule";
import type { PlayedNote, ScheduleOptions } from "../playback/schedule";
import { ascii, bytesOf, chunk, u16, vlq } from "./bytes";

/** The MIDI channel drums are struck on, which is 10 counted from one. */
const DRUM_CHANNEL = 9;

const META = 0xff;
const META_TEXT_TRACK_NAME = 0x03;
const META_TEMPO = 0x51;
const META_TIME = 0x58;
const META_KEY = 0x59;
const META_END = 0x2f;

/** One thing that happens at one tick, before the deltas are worked out. */
interface Moment {
  readonly tick: number;
  /** Note-off sorts before note-on at the same tick, so a repeated key is not cut short. */
  readonly order: number;
  readonly bytes: readonly number[];
}

/** The same choices playback offers: how fast where nothing says, and whether to repeat. */
export type MidiWriteOptions = ScheduleOptions;

function meta(kind: number, body: readonly number[]): readonly number[] {
  return [META, kind, body.length, ...body];
}

/** Microseconds per quarter note, which is how MIDI states a speed. */
function tempoMeta(bpm: number, unitTicks: number): readonly number[] {
  const quarters = (bpm * unitTicks) / PPQ;
  const micros = Math.round(60000000 / Math.max(quarters, 1));
  return meta(META_TEMPO, [(micros >> 16) & 0xff, (micros >> 8) & 0xff, micros & 0xff]);
}

/**
 * A metre, with its lower number as the power of two MIDI writes it as.
 *
 * The two trailing bytes are the metronome and how many demisemiquavers go to a quarter:
 * 24 and 8 are the values the specification gives as ordinary, and nothing in this editor
 * asks for anything else.
 */
function timeMeta(beats: number, beatType: number): readonly number[] {
  const power = Math.min(Math.max(Math.round(Math.log2(Math.max(beatType, 1))), 0), 7);
  return meta(META_TIME, [Math.min(Math.max(beats, 1), 255), power, 24, 8]);
}

function keyMeta(fifths: number, minor: boolean): readonly number[] {
  const signed = Math.min(Math.max(fifths, -7), 7);
  return meta(META_KEY, [signed & 0xff, minor ? 1 : 0]);
}

/**
 * The conductor track: what is true of the piece rather than of any player in it.
 *
 * Each is written where it changes, in the played order of the bars, so a repeat that
 * encloses a change of metre states it again the second time through -- which is what
 * playing it means.
 */
function conductor(score: Score, bars: readonly { index: number; ticks: number }[]): Moment[] {
  const moments: Moment[] = [
    { tick: 0, order: 0, bytes: meta(META_TEXT_TRACK_NAME, ascii(score.meta.title)) },
  ];
  let time = "";
  let key = "";
  let tempo = "";

  for (const bar of bars) {
    const at = resolveAttributes(score, bar.index);
    const timeKey = `${at.time.beats}/${at.time.beatType}`;
    const keyKey = `${at.key.fifths}${at.key.mode}`;
    const tempoKey = at.tempo === null ? tempo : `${at.tempo.bpm}${ticksOf(at.tempo.unit)}`;

    if (timeKey !== time)
      moments.push({ tick: bar.ticks, order: 0, bytes: timeMeta(at.time.beats, at.time.beatType) });
    if (keyKey !== key)
      moments.push({
        tick: bar.ticks,
        order: 0,
        bytes: keyMeta(at.key.fifths, at.key.mode === "minor"),
      });
    if (at.tempo !== null && tempoKey !== tempo) {
      moments.push({
        tick: bar.ticks,
        order: 0,
        bytes: tempoMeta(at.tempo.bpm, ticksOf(at.tempo.unit)),
      });
    }
    time = timeKey;
    key = keyKey;
    tempo = tempoKey;
  }

  if (tempo === "") moments.push({ tick: 0, order: 0, bytes: tempoMeta(120, PPQ) });
  return moments;
}

/**
 * Which channel each part plays on.
 *
 * A drum part takes channel 10, where every General MIDI player reads a key number as a
 * drum rather than as a pitch; the rest take the others in order. There are fifteen of
 * those and a score can have more parts than that, so they wrap: two parts sharing a
 * channel sound right as long as they do not need different sounds at the same moment,
 * which is the same compromise every sequencer makes.
 */
function channelsOf(score: Score): ReadonlyMap<string, number> {
  const channels = new Map<string, number>();
  let next = 0;
  for (const part of score.parts) {
    if (part.instrument.percussion !== null) {
      channels.set(part.id, DRUM_CHANNEL);
      continue;
    }
    if (next === DRUM_CHANNEL) next += 1;
    if (next > 15) next = 0;
    channels.set(part.id, next);
    next += 1;
  }
  return channels;
}

/**
 * The programs a part is heard on: what it opens holding, everything it later takes up, and
 * the plucked strings wherever it puts its bow down.
 *
 * A doubling sounds as well as prints, so the bar that hands the player another instrument
 * sets another program. The played bars are walked rather than the written ones, and each is
 * asked what is in the player's hands there rather than what it states, because a repeat
 * enclosing a doubling goes back to a bar holding the instrument the part opened on -- which
 * is what the notes of that bar are already being sounded as.
 *
 * Plucking is not a bar's but a note's, so it is read off the notes: General MIDI keeps a
 * plucked string as a program of its own, set where the first plucked note begins and put
 * back to the instrument where the next bowed one does. A part that ends plucked is handed
 * its instrument back after its last note, so that a reader asking what the track plays is
 * told a violin and not the way it happened to be playing when the piece stopped. A trumpet
 * with its mute on is the one other program the standard has for a way of playing, and is
 * set and put back the same way; any other muted instrument, and a bow at the bridge, has
 * none and keeps its own.
 *
 * A drum part makes none. Channel 10 reads a key number as a drum whatever program is set,
 * and a kit is a part's own for the whole piece, so there is nothing for it to change to.
 */
function programsOf(
  score: Score,
  part: Part,
  bars: readonly { index: number; ticks: number }[],
  notes: readonly PlayedNote[],
  channel: number,
): readonly Moment[] {
  if (channel === DRUM_CHANNEL) return [];
  const change = (tick: number, program: number): Moment => ({
    tick,
    order: 0,
    bytes: [0xc0 | channel, program & 0x7f],
  });

  // A bar comes before the notes that begin on its line, so the instrument is in the
  // player's hands before anything is asked of how it is played.
  const wanted = [
    ...bars.map((bar) => ({
      tick: bar.ticks,
      taken: true,
      program: instrumentAt(score, part, bar.index).midiProgram,
    })),
    ...notes.map((note) => ({
      tick: note.startTicks,
      taken: false,
      program: note.plucked
        ? PIZZICATO_PROGRAM
        : note.muted
          ? mutedProgram(note.program)
          : note.program,
    })),
  ].sort((left, right) =>
    left.tick === right.tick ? Number(right.taken) - Number(left.taken) : left.tick - right.tick,
  );

  const moments: Moment[] = [];
  let held = part.instrument.midiProgram;
  let sounding = held;
  if (sounding !== null) moments.push(change(0, sounding));
  for (const { tick, taken, program } of wanted) {
    // A part whose instrument names no program is left on whatever is sounding: MIDI has no
    // way of saying nothing, and silencing it would be saying something else.
    if (program === null) continue;
    if (taken) {
      // A bar says something only where the instrument changes hands. One the player is
      // plucking through leaves the channel as the notes have set it.
      if (program === held) continue;
      held = program;
    }
    if (program === sounding) continue;
    sounding = program;
    moments.push(change(tick, program));
  }
  if (held !== null && sounding !== held) {
    const end = notes.reduce(
      (last, note) => Math.max(last, note.startTicks + Math.max(note.durationTicks, 1)),
      0,
    );
    moments.push(change(end, held));
  }
  return moments;
}

/** Absolute moments turned into the delta-timed bytes of a track chunk. */
function trackOf(moments: readonly Moment[]): readonly number[] {
  const sorted = [...moments].sort((left, right) =>
    left.tick === right.tick ? left.order - right.order : left.tick - right.tick,
  );
  const body: number[] = [];
  let clock = 0;
  for (const moment of sorted) {
    body.push(...vlq(moment.tick - clock), ...moment.bytes);
    clock = moment.tick;
  }
  body.push(...vlq(0), META, META_END, 0);
  return chunk("MTrk", body);
}

/** A score as the bytes of a `.mid` file. */
export function toMidi(score: Score, options: MidiWriteOptions = {}): Uint8Array {
  const schedule = scheduleOf(score, options);
  const channels = channelsOf(score);

  const tracks: (readonly number[])[] = [trackOf(conductor(score, schedule.bars))];

  for (const part of score.parts) {
    const channel = channels.get(part.id) ?? 0;
    const moments: Moment[] = [
      { tick: 0, order: 0, bytes: meta(META_TEXT_TRACK_NAME, ascii(part.name)) },
    ];
    const notes = schedule.notes.filter((note) => note.partId === part.id);
    for (const note of notes) {
      const key = Math.min(Math.max(Math.round(note.midi), 0), 127);
      const velocity = Math.min(Math.max(Math.round(note.velocity * 127), 1), 127);
      const end = note.startTicks + Math.max(note.durationTicks, 1);
      moments.push({ tick: note.startTicks, order: 1, bytes: [0x90 | channel, key, velocity] });
      moments.push({ tick: end, order: 0, bytes: [0x80 | channel, key, 0] });
    }
    // After the notes: a program change and a note-off share an order, and the sort keeps
    // them in the order they were made, so the key that was down is released first.
    moments.push(...programsOf(score, part, schedule.bars, notes, channel));

    tracks.push(trackOf(moments));
  }

  const header = chunk("MThd", [...u16(1), ...u16(tracks.length), ...u16(PPQ)]);
  return bytesOf(header, ...tracks);
}
